// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { resetXCheckoutState } from "@conklin/App/Handlers/Checkout/xCheckout";
import TwoButtonModal, { TwoButtonModalStyles } from "@conklin/Overrides/Components/TwoButtonModal";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import loadCurrentAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/LoadCurrentAccount";
import {
    getReviewAndPayCartState,
    isCartLoadedWithoutLines,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getCurrentPage, getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getReviewAndPayPromotions } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import loadDataIfNeeded from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/LoadDataIfNeeded";
import setCartId from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetCartId";
import setInitialValues from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetInitialValues";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import setPaymentDetailsState from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetPaymentDetailsState";
import Page from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { createContext, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = getQueryStrings(state);
    const cartState = getReviewAndPayCartState(state);
    const { paymentDetailsState } = state.pages.onePageCheckout;
    const promotionsDataView = getReviewAndPayPromotions(state);
    const location = getCurrentPage(state);
    const expand = state.data.carts.cartApiParameter?.expand || [];

    const isLoadDataNeeded =
        ((isCartLoadedWithoutLines(cartState) ||
            !cartState.value?.carriers ||
            !expand.includes("shipping") ||
            !expand.includes("tax")) &&
            !cartState.isLoading) ||
        (!promotionsDataView.value && !promotionsDataView.isLoading);

    return {
        cartId,
        cartState,
        isLoadDataNeeded: isLoadDataNeeded || !getCurrentCountries(state),
        isUpdatingCustomer: state.pages.onePageCheckout.isUpdatingCustomer,
        shouldLoadAccount: !getCurrentAccountState(state).value,
        location,
        paymentDetailsState,
        // XNG-Changes
        isPlacingOrder: state.pages.onePageCheckout.isPlacingOrder,
        isLoading:
            !cartState.value ||
            cartState.isLoading ||
            state.pages.onePageCheckoutConfirmation.isPreloadingData ||
            state.pages.onePageCheckout.isSavingShipping,
        cartEmpty: !cartState.isLoading && cartState.value && !cartState.value.cartLines?.length,
        homePageUrl: getPageLinkByPageType(state, "HomePage")?.url,
        confirmationPageUrl:
            getPageLinkByPageType(state, "OnePageCheckoutConfirmationPage")?.url || "/OnePageCheckoutConfirmation",
    };
};

const mapDispatchToProps = {
    loadDataIfNeeded,
    setInitialValues,
    setCartId,
    loadCurrentAccount,
    setPaymentDetailsState,
    // XNG-Changes
    resetXCheckoutState,
};

type Props = PageProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasHistory;

type Validator = () => Promise<boolean>;

type State = {
    formSubmitAttempted: boolean;
    validators: {
        [key: string]: Validator | undefined;
    };
    // XNG-Changes
    formIds?: {
        shippingFormId?: string;
        billingFormId?: string;
        ccBillingFormId?: string;
    };
};

export const OnePageCheckoutFormContext = createContext<State>({
    formSubmitAttempted: false,
    validators: {},
});

export interface OnePageCheckoutPageStyles {
    modal?: TwoButtonModalStyles;
}

export const onePageCheckoutPageStyles: OnePageCheckoutPageStyles = {};

const styles = onePageCheckoutPageStyles;

const OnePageCheckoutPage = ({
    id,
    cartId,
    isLoadDataNeeded,
    isUpdatingCustomer,
    shouldLoadAccount,
    loadDataIfNeeded,
    setInitialValues,
    setCartId,
    loadCurrentAccount,
    location,
    paymentDetailsState,
    setPaymentDetailsState,
    // XNG-Changes
    history,
    isPlacingOrder,
    isLoading,
    cartEmpty,
    homePageUrl,
    confirmationPageUrl,
    resetXCheckoutState,
}: Props) => {
    // XNG-Changes
    const [leaveCheckoutModalVisible, setLeaveCheckoutModalVisible] = useState(false);
    const isDone = useRef<string>("");
    const dispatch = useAppDispatch();
    const [state] = useState<State>({
        formSubmitAttempted: false,
        validators: {},
    });

    useEffect(() => {
        if (isLoadDataNeeded) {
            loadDataIfNeeded({ cartId, onSuccess: () => setInitialValues() });
        }
    });

    useEffect(() => {
        setCartId({ cartId });
        if (!isLoadDataNeeded) {
            setInitialValues();
        }
        if (shouldLoadAccount) {
            loadCurrentAccount();
        }

        // XNG-Changes: Confirmation Popup
        const _push = history.push;
        const _replace = history.replace;

        history.push = async (url, state: any) => {
            const res = await onChangeState(url, state?.passModal);
            if (res) {
                _push(url, state);
            }
        };
        history.replace = async (url, state: any) => {
            const res = await onChangeState(url, state?.passModal);
            if (res) {
                _replace(url, state);
            }
        };
        return () => {
            history.push = _push;
            history.replace = _replace;

            resetXCheckoutState();
        };
    }, []);

    // <XNG-Changes>
    const onChangeState = async (url: string, passModal: boolean) => {
        if (url.indexOf(confirmationPageUrl) > -1 || passModal) {
            return true;
        }

        isDone.current = "";
        setLeaveCheckoutModalVisible(true);
        await waitFor(() => {
            return isDone.current !== "";
        });
        return isDone.current === "Accept";
    };

    useEffect(() => {
        if (cartEmpty) {
            history.push(homePageUrl || "/", { passModal: true });
        }
    }, [cartEmpty]);
    // </XNG-Changes>

    useEffect(() => {
        if (paymentDetailsState === "complete") {
            setPaymentDetailsState({ paymentDetailsState: "active" });
        }
    }, [location]);

    return (
        <Page>
            {/* XNG-Changes: add  isLoading || isPlacingOrder */}
            <LoadingOverlay loading={isUpdatingCustomer || isLoading || isPlacingOrder}>
                <OnePageCheckoutFormContext.Provider value={state}>
                    <Zone zoneName="Content" contentId={id} />
                </OnePageCheckoutFormContext.Provider>
            </LoadingOverlay>

            {/* XNG-Changes */}
            <TwoButtonModal
                headlineText={siteMessage("Custom_CheckoutLeaveWarningModal_Title")}
                messageText={siteMessage("Custom_CheckoutLeaveWarningModal_Message")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Leave Checkout")}
                submitTestSelector="leaveCheckout_Button"
                modalIsOpen={leaveCheckoutModalVisible}
                onCancel={() => {
                    isDone.current = "Reject";
                    setLeaveCheckoutModalVisible(false);
                }}
                onSubmit={() => {
                    isDone.current = "Accept";
                    // XNG-Change: clear error message
                    dispatch({
                        type: "Pages/OnePageCheckout/SetPlaceOrderErrorMessage",
                        errorMessage: undefined,
                    });
                    setLeaveCheckoutModalVisible(false);
                }}
                // XNG-Changes
                extendedStyles={styles.modal}
            />
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(OnePageCheckoutPage)),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;
